import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { bibleService } from '../../../services/bibleService';
import BookChapterModal from './BookChapterModal';

const BookChapterSelector = ({ 
  selectedBook, 
  selectedChapter, 
  onBookChange, 
  onChapterChange,
  onPreviousChapter,
  onNextChapter,
  versionSelector,
  searchBar,
  toolbarButtons,
  localizedBookName,
  className = '' 
}) => {
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalDefaultView, setModalDefaultView] = useState('book');

  useEffect(() => {
    const loadBooks = async () => {
      try {
        setLoading(true);
        setError(null);
        const { data, error } = await bibleService?.getBibleBooks();
        if (error) { setError('Failed to load Bible books'); return; }
        setBooks(data || []);
      } catch (err) {
        setError('Failed to load Bible books');
      } finally {
        setLoading(false);
      }
    };
    loadBooks();
  }, []);

  const handleOpenBookModal = () => {
    setIsModalOpen(false);
    setTimeout(() => { setModalDefaultView('book'); setIsModalOpen(true); }, 0);
  };

  const handleOpenChapterModal = () => {
    setIsModalOpen(false);
    setTimeout(() => { setModalDefaultView('chapter'); setIsModalOpen(true); }, 0);
  };

  const handleNavigate = (book, chapter) => {
    if (book !== selectedBook) onBookChange(book);
    if (chapter !== selectedChapter) onChapterChange(chapter);
  };

  const currentBook = books?.find(book => book?.name === selectedBook);
  const maxChapters = currentBook ? currentBook?.chapter_count : 1;
  const canGoPrevious = selectedChapter > 1;
  const canGoNext = selectedChapter < maxChapters;

  if (loading) {
    return (
      <div className={`bg-card rounded-lg border border-border p-2 ${className}`}>
        <div className="flex items-center gap-1">
          <div className="w-24 h-7 bg-muted rounded animate-pulse"></div>
          <div className="w-6 h-7 bg-muted rounded animate-pulse"></div>
          <div className="w-16 h-7 bg-muted rounded animate-pulse"></div>
          <div className="w-6 h-7 bg-muted rounded animate-pulse"></div>
          <div className="w-20 h-7 bg-muted rounded animate-pulse"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`bg-card rounded-lg border border-destructive p-2 ${className}`}>
        <div className="flex items-center space-x-2">
          <Icon name="AlertCircle" size={14} className="text-destructive" />
          <span className="text-destructive text-xs">{error}</span>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Outer flex row: scrollable nav controls + search outside overflow */}
      <div className={`flex items-center gap-1 w-full ${className}`}>
        {/* Horizontal-scroll wrapper for mobile — nav buttons + version selector scroll together */}
        <div className="flex-1 overflow-x-auto scrollbar-hide" style={{ WebkitOverflowScrolling: 'touch' }}>
          <div className="flex items-center gap-1 min-w-max">
            {/* Book Selector Button */}
            <button
              onClick={handleOpenBookModal}
              className="flex items-center gap-1 px-2 py-1 h-8 text-xs font-medium rounded border border-border bg-background hover:bg-muted transition-colors flex-shrink-0 max-w-[140px] sm:max-w-[180px]"
              title={localizedBookName || selectedBook}
            >
              <span className="truncate max-w-[100px] sm:max-w-[140px]">{localizedBookName || selectedBook}</span>
              <Icon name="ChevronDown" size={12} className="flex-shrink-0 ml-auto" />
            </button>

            {/* Chapter Navigation */}
            <button
              onClick={onPreviousChapter}
              disabled={!canGoPrevious}
              title="Previous Chapter"
              className="w-8 h-8 flex-shrink-0 flex items-center justify-center rounded border border-border bg-background hover:bg-muted transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Icon name="ChevronLeft" size={14} />
            </button>

            <button
              onClick={handleOpenChapterModal}
              className="flex items-center gap-1 px-2 py-1 h-8 text-xs font-medium rounded border border-border bg-background hover:bg-muted transition-colors flex-shrink-0 min-w-[44px]"
            >
              <span>{selectedChapter}</span>
              <Icon name="ChevronDown" size={12} className="flex-shrink-0" />
            </button>

            <button
              onClick={onNextChapter}
              disabled={!canGoNext}
              title="Next Chapter"
              className="w-8 h-8 flex-shrink-0 flex items-center justify-center rounded border border-border bg-background hover:bg-muted transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Icon name="ChevronRight" size={14} />
            </button>

            {/* Version selector — inside scroll row, left-aligned beside chapter selector */}
            {versionSelector && (
              <div className="flex-shrink-0">
                {versionSelector}
              </div>
            )}
          </div>
        </div>

        {/* Search — outside overflow container so dropdowns are not clipped */}
        {searchBar && (
          <div className="flex-shrink-0">
            {searchBar}
          </div>
        )}

        {/* Toolbar buttons (audio, fullscreen, study tools) */}
        {toolbarButtons && (
          <div className="flex items-center gap-1 flex-shrink-0">
            {toolbarButtons}
          </div>
        )}
      </div>

      <BookChapterModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        books={books}
        selectedBook={selectedBook}
        selectedChapter={selectedChapter}
        onNavigate={handleNavigate}
        defaultView={modalDefaultView}
      />
    </>
  );
};

export default BookChapterSelector;